import { ReactNode, useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";
import { SplitWords } from "@/components/Reveal";
import { useI18n } from "@/i18n/I18nProvider";

interface DetailHeroProps {
  image?: string | null;
  imageAlt: string;
  credit?: string | null;
  creditUrl?: string | null;
  eyebrow?: ReactNode;
  title: string;
  titleLang?: string;
  subtitle?: ReactNode;
}

/** Full-bleed image header with a slow settle-in zoom and gentle parallax. */
const DetailHero = ({ image, imageAlt, credit, creditUrl, eyebrow, title, titleLang, subtitle }: DetailHeroProps) => {
  const { m } = useI18n();
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      if (layerRef.current && y < window.innerHeight) {
        layerRef.current.style.transform = `translate3d(0, ${y * 0.25}px, 0)`;
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="on-dark relative h-[58vh] min-h-[420px] max-h-[680px] overflow-hidden bg-[hsl(156,35%,5%)]">
      <div ref={layerRef} className="absolute inset-0 will-change-transform">
        {image ? (
          <img src={image} alt={imageAlt} className="hero-settle w-full h-full object-cover" />
        ) : (
          <div className="hero-settle absolute inset-0 bg-gradient-to-br from-primary/25 via-secondary to-[hsl(156,35%,5%)]" />
        )}
      </div>

      {/* Dark scrim keeps the title legible in both themes, then melts into the page */}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,15,13,0.92)] via-[rgba(10,15,13,0.35)] to-[rgba(10,15,13,0.15)]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[rgba(10,15,13,0.55)] via-transparent to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />

      {credit && creditUrl && (
        <a
          href={creditUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute right-4 top-4 z-10 flex max-w-[calc(100%-2rem)] items-start gap-1.5 rounded-full bg-black/75 backdrop-blur-md px-3 py-1.5 text-xs text-white ring-1 ring-white/15 transition-colors hover:bg-black/90"
        >
          <span className="min-w-0 break-words">{m.common.image}: {credit}</span>
          <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
        </a>
      )}

      <div className="absolute inset-x-0 bottom-0 pb-20 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {eyebrow && <div className="hero-fade mb-5 flex flex-wrap items-center gap-3">{eyebrow}</div>}
          <h1 className="font-display font-medium text-[3rem] md:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-white max-w-4xl text-balance" lang={titleLang}>
            <SplitWords text={title} baseDelay={150} />
          </h1>
          {subtitle && (
            <div className="hero-fade mt-4 font-mono text-sm text-white/70" style={{ animationDelay: "450ms" }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailHero;
